import { useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { X, Sparkles, Chrome, Globe, Search, Mail, FileText, Twitter, Linkedin } from 'lucide-react'

// Clichés to detect with their suggestions
const CLICHE_DATABASE = [
    { phrase: "at the end of the day", suggestion: "ultimately", severity: "high" },
    { phrase: "think outside the box", suggestion: "be creative", severity: "high" },
    { phrase: "touch base", suggestion: "connect", severity: "medium" },
    { phrase: "move the needle", suggestion: "make progress", severity: "medium" },
    { phrase: "low-hanging fruit", suggestion: "easy wins", severity: "medium" },
    { phrase: "circle back", suggestion: "revisit", severity: "medium" },
    { phrase: "synergy", suggestion: "collaboration", severity: "low" },
    { phrase: "take it to the next level", suggestion: "improve", severity: "high" },
    { phrase: "game changer", suggestion: "breakthrough", severity: "medium" },
    { phrase: "paradigm shift", suggestion: "major change", severity: "high" },
    { phrase: "best practices", suggestion: "proven methods", severity: "low" },
    { phrase: "win-win", suggestion: "mutual benefit", severity: "medium" },
    { phrase: "deep dive", suggestion: "thorough analysis", severity: "low" },
    { phrase: "on the same page", suggestion: "aligned", severity: "medium" },
    { phrase: "pushing the envelope", suggestion: "innovating", severity: "high" },
]

// Platform configurations
const PLATFORMS = {
    gmail: {
        name: 'Gmail',
        icon: Mail,
        url: 'mail.google.com/compose',
        bgColor: 'bg-white',
        textColor: 'text-gray-800',
        placeholder: 'Compose your email...',
        headerBg: 'bg-gray-100',
        inputBg: 'bg-white border border-gray-200',
    },
    docs: {
        name: 'Google Docs',
        icon: FileText,
        url: 'docs.google.com/document',
        bgColor: 'bg-white',
        textColor: 'text-gray-800',
        placeholder: 'Start typing your document...',
        headerBg: 'bg-blue-50',
        inputBg: 'bg-white',
    },
    linkedin: {
        name: 'LinkedIn',
        icon: Linkedin,
        url: 'linkedin.com/feed',
        bgColor: 'bg-white',
        textColor: 'text-gray-800',
        placeholder: 'Start a post...',
        headerBg: 'bg-white border-b border-gray-200',
        inputBg: 'bg-gray-50 border border-gray-200 rounded-xl',
    },
    twitter: {
        name: 'X (Twitter)',
        icon: Twitter,
        url: 'x.com/compose',
        bgColor: 'bg-black',
        textColor: 'text-white',
        placeholder: "What's happening?",
        headerBg: 'bg-black border-b border-gray-800',
        inputBg: 'bg-black',
    },
}

// Detect clichés in text
function detectCliches(text) {
    const lowerText = text.toLowerCase()
    const found = []

    CLICHE_DATABASE.forEach(cliche => {
        let startIndex = 0
        let index
        while ((index = lowerText.indexOf(cliche.phrase, startIndex)) !== -1) {
            found.push({
                ...cliche,
                start: index,
                end: index + cliche.phrase.length,
                original: text.slice(index, index + cliche.phrase.length)
            })
            startIndex = index + 1
        }
    })

    return found.sort((a, b) => a.start - b.start)
}

// Extension Popup
function ExtensionPopup({ detectionCount, platform }) {
    return (
        <div className="w-64 bg-slate-900 rounded-xl border border-white/10 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b border-white/10">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                        <Sparkles className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="font-semibold text-white text-sm">InkSpot</span>
                </div>
                <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-full">
                    Pro
                </span>
            </div>

            <div className="p-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                        <div className="text-xl font-bold text-cyan-400">{detectionCount}</div>
                        <div className="text-[10px] text-white/50">Clichés Found</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                        <div className="text-xl font-bold text-white">1</div>
                        <div className="text-[10px] text-white/50">Page Active</div>
                    </div>
                </div>

                <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                    <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-cyan-400" />
                        <div>
                            <div className="text-xs text-white">{platform.url.split('/')[0]}</div>
                            <div className="text-[10px] text-green-400">Active</div>
                        </div>
                    </div>
                    <div className="w-8 h-4 bg-cyan-500 rounded-full relative">
                        <div className="absolute right-0.5 top-0.5 w-3 h-3 bg-white rounded-full"></div>
                    </div>
                </div>

                <button className="w-full flex items-center justify-center gap-2 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg text-xs">
                    <Search className="w-3.5 h-3.5" />
                    Analyze Page
                </button>
            </div>
        </div>
    )
}

// Highlighted Text Display
function HighlightedText({ text, cliches, onClicheHover, hoveredCliche }) {
    if (!text) return <span className="text-gray-400 italic">Start typing to see cliché detection...</span>
    if (cliches.length === 0) return <span>{text}</span>

    const elements = []
    let lastIndex = 0

    cliches.forEach((cliche, idx) => {
        if (cliche.start > lastIndex) {
            elements.push(<span key={`t-${idx}`}>{text.slice(lastIndex, cliche.start)}</span>)
        }

        const isHovered = hoveredCliche === idx
        const severityColors = {
            high: isHovered ? 'bg-red-500/60' : 'bg-red-500/30',
            medium: isHovered ? 'bg-yellow-500/60' : 'bg-yellow-500/30',
            low: isHovered ? 'bg-blue-500/60' : 'bg-blue-500/30',
        }

        elements.push(
            <span
                key={`h-${idx}`}
                className={`${severityColors[cliche.severity]} border-b-2 ${cliche.severity === 'high' ? 'border-red-500' : cliche.severity === 'medium' ? 'border-yellow-500' : 'border-blue-500'} px-0.5 rounded cursor-pointer transition-colors relative group`}
                onMouseEnter={() => onClicheHover(idx)}
                onMouseLeave={() => onClicheHover(null)}
            >
                {cliche.original}
                {isHovered && (
                    <span className="absolute bottom-full left-0 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
                        Try: <span className="text-cyan-400 font-medium">"{cliche.suggestion}"</span>
                    </span>
                )}
            </span>
        )

        lastIndex = cliche.end
    })

    if (lastIndex < text.length) {
        elements.push(<span key="t-end">{text.slice(lastIndex)}</span>)
    }

    return <>{elements}</>
}

// Main Demo Overlay
function DemoOverlay({ onClose }) {
    const [activePlatform, setActivePlatform] = useState('gmail')
    const [text, setText] = useState('')
    const [hoveredCliche, setHoveredCliche] = useState(null)

    const platform = PLATFORMS[activePlatform]
    const cliches = useMemo(() => detectCliches(text), [text])

    const sampleTexts = {
        gmail: "At the end of the day, we need to think outside the box and touch base with the team to move the needle on this project.",
        docs: "This paradigm shift represents a game changer for our industry. We should circle back on the low-hanging fruit opportunities.",
        linkedin: "Excited to share that our team is pushing the envelope and taking it to the next level! Looking for synergy with like-minded professionals.",
        twitter: "Best practices and deep dive into how to be on the same page with your audience. It's a win-win!",
    }

    const handleTrySample = () => {
        setText(sampleTexts[activePlatform])
    }

    const handleClear = () => {
        setText('')
    }

    return createPortal(
        <div
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-5xl bg-slate-900 rounded-2xl border border-white/20 shadow-2xl overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10 bg-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                            <Chrome className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white">Try InkSpot Extension</h3>
                            <p className="text-sm text-white/50">Type to see real-time cliché detection</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Platform Tabs */}
                <div className="flex gap-1 p-2 bg-slate-800/50 border-b border-white/10 overflow-x-auto">
                    {Object.entries(PLATFORMS).map(([key, p]) => {
                        const Icon = p.icon
                        return (
                            <button
                                key={key}
                                onClick={() => { setActivePlatform(key); setText('') }}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activePlatform === key
                                        ? 'bg-cyan-500 text-white'
                                        : 'text-white/60 hover:text-white hover:bg-white/10'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                {p.name}
                            </button>
                        )
                    })}
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="flex flex-col lg:flex-row gap-6">
                        {/* Browser Simulation */}
                        <div className="flex-1 min-w-0">
                            <div className="bg-slate-800 rounded-xl overflow-hidden border border-white/10">
                                {/* Browser Chrome */}
                                <div className="flex items-center gap-2 px-4 py-3 bg-slate-950 border-b border-white/10">
                                    <div className="flex gap-1.5">
                                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    </div>
                                    <div className="flex-1 mx-4">
                                        <div className="flex items-center gap-2 bg-slate-700 rounded-lg px-3 py-1.5 text-sm text-white/70">
                                            🔒 {platform.url}
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                                            <Sparkles className="w-4 h-4 text-white" />
                                        </div>
                                        {cliches.length > 0 && (
                                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                                                {cliches.length}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Platform Content */}
                                <div className={`p-4 ${platform.bgColor} min-h-[300px]`}>
                                    {/* Gmail Style */}
                                    {activePlatform === 'gmail' && (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2 text-sm text-gray-600 border-b pb-2">
                                                <span className="font-medium">To:</span>
                                                <span className="text-gray-500">team@company.com</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 border-b pb-2">
                                                <span className="font-medium">Subject:</span>
                                                <span className="text-gray-500">Project Update</span>
                                            </div>
                                            <textarea
                                                value={text}
                                                onChange={(e) => setText(e.target.value)}
                                                placeholder={platform.placeholder}
                                                className="w-full h-40 p-2 text-gray-800 bg-white border-none outline-none resize-none text-sm"
                                            />
                                            <div className={`min-h-[60px] p-2 ${platform.inputBg} rounded text-sm leading-relaxed ${platform.textColor}`}>
                                                <HighlightedText
                                                    text={text}
                                                    cliches={cliches}
                                                    onClicheHover={setHoveredCliche}
                                                    hoveredCliche={hoveredCliche}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Google Docs Style */}
                                    {activePlatform === 'docs' && (
                                        <div className="bg-white shadow-sm rounded p-6 min-h-[280px]">
                                            <textarea
                                                value={text}
                                                onChange={(e) => setText(e.target.value)}
                                                placeholder={platform.placeholder}
                                                className="w-full h-32 text-gray-800 bg-transparent border-none outline-none resize-none text-sm"
                                            />
                                            <div className="mt-4 pt-4 border-t min-h-[60px] text-sm leading-relaxed text-gray-800">
                                                <HighlightedText
                                                    text={text}
                                                    cliches={cliches}
                                                    onClicheHover={setHoveredCliche}
                                                    hoveredCliche={hoveredCliche}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* LinkedIn Style */}
                                    {activePlatform === 'linkedin' && (
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500"></div>
                                                <div>
                                                    <div className="font-medium text-gray-800">Your Name</div>
                                                    <div className="text-xs text-gray-500">Post to Anyone</div>
                                                </div>
                                            </div>
                                            <textarea
                                                value={text}
                                                onChange={(e) => setText(e.target.value)}
                                                placeholder={platform.placeholder}
                                                className={`w-full h-32 p-3 text-gray-800 ${platform.inputBg} outline-none resize-none text-sm`}
                                            />
                                            <div className={`${platform.inputBg} p-3 min-h-[60px] text-sm leading-relaxed text-gray-800`}>
                                                <HighlightedText
                                                    text={text}
                                                    cliches={cliches}
                                                    onClicheHover={setHoveredCliche}
                                                    hoveredCliche={hoveredCliche}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Twitter/X Style */}
                                    {activePlatform === 'twitter' && (
                                        <div className="space-y-4 bg-black p-4 rounded">
                                            <div className="flex items-start gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex-shrink-0"></div>
                                                <div className="flex-1">
                                                    <textarea
                                                        value={text}
                                                        onChange={(e) => setText(e.target.value)}
                                                        placeholder={platform.placeholder}
                                                        className="w-full h-24 bg-transparent text-white border-none outline-none resize-none text-lg"
                                                    />
                                                </div>
                                            </div>
                                            <div className="ml-13 pl-13 border-l border-gray-800 ml-12 pl-4 min-h-[40px] text-white text-lg leading-relaxed">
                                                <HighlightedText
                                                    text={text}
                                                    cliches={cliches}
                                                    onClicheHover={setHoveredCliche}
                                                    hoveredCliche={hoveredCliche}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-2 p-3 bg-slate-800 border-t border-white/10">
                                    <button
                                        onClick={handleTrySample}
                                        className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 text-sm rounded-lg hover:bg-cyan-500/30 transition-colors"
                                    >
                                        Try Sample Text
                                    </button>
                                    <button
                                        onClick={handleClear}
                                        className="px-3 py-1.5 bg-white/10 text-white/70 text-sm rounded-lg hover:bg-white/20 transition-colors"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Extension Popup */}
                        <div className="lg:w-64 flex-shrink-0">
                            <ExtensionPopup detectionCount={cliches.length} platform={platform} />

                            {/* Detected Clichés List */}
                            {cliches.length > 0 && (
                                <div className="mt-4 bg-slate-800 rounded-xl p-3 border border-white/10">
                                    <h4 className="text-sm font-medium text-white mb-2">Detected Issues</h4>
                                    <div className="space-y-2 max-h-40 overflow-y-auto">
                                        {cliches.map((c, i) => (
                                            <div
                                                key={i}
                                                className={`p-2 rounded text-xs ${hoveredCliche === i ? 'bg-white/10' : 'bg-slate-700/50'}`}
                                                onMouseEnter={() => setHoveredCliche(i)}
                                                onMouseLeave={() => setHoveredCliche(null)}
                                            >
                                                <div className="text-white/80">"{c.original}"</div>
                                                <div className="text-cyan-400 mt-1">→ {c.suggestion}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-white/10 bg-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-white/50 text-center sm:text-left">
                        Real-time detection on Gmail, Docs, LinkedIn, X & more
                    </p>
                    <a
                        href="https://chromewebstore.google.com/detail/inkspot-ai-writing-assist/bjifgkinjedaikncmccpihmclmehhmoo"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg text-sm hover:opacity-90 flex items-center justify-center gap-2"
                    >
                        <Chrome className="w-4 h-4" />
                        Install Free Extension
                    </a>
                </div>
            </div>
        </div>,
        document.body
    )
}

// Demo Button (exported)
export function ExtensionDemoButton({ variant = 'primary', className = '' }) {
    const [showDemo, setShowDemo] = useState(false)
    const handleClose = useCallback(() => setShowDemo(false), [])

    const baseClass = variant === 'primary'
        ? 'px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg hover:opacity-90 flex items-center gap-2'
        : 'px-4 py-2 bg-slate-800 border border-white/10 text-white font-medium rounded-lg hover:bg-slate-700 flex items-center gap-2'

    return (
        <>
            <button onClick={() => setShowDemo(true)} className={`${baseClass} ${className}`} type="button">
                <Chrome className="w-4 h-4" />
                Try Extension Demo
            </button>
            {showDemo && <DemoOverlay onClose={handleClose} />}
        </>
    )
}

// Default export
export default function ExtensionDemo({ isOpen, onClose }) {
    if (!isOpen) return null
    return <DemoOverlay onClose={onClose} />
}
